type EventValue = {
  type?: string;
  value?: string;
};

// Duck-typed on purpose — avoids importing @sentry/react-native's real Event
// type so tests can pass plain object literals with no SDK mocking.
export type SentryEventLike = {
  message?: string;
  request?: {
    url?: string;
    method?: string;
  };
  tags?: Record<string, string | undefined>;
  contexts?: {
    response?: {
      status_code?: number;
      url?: string;
    };
    trace?: {
      data?: {
        url?: string;
      };
    };
  };
  exception?: {
    values?: EventValue[];
  };
};

export const extractSentryEventUrl = (event: SentryEventLike): string => {
  return (
    event.request?.url ??
    event.contexts?.response?.url ??
    event.contexts?.trace?.data?.url ??
    ''
  );
};

export const extractSentryEventStatusCode = (
  event: SentryEventLike
): number | undefined => {
  const contextStatusCode = event.contexts?.response?.status_code;
  if (typeof contextStatusCode === 'number') {
    return contextStatusCode;
  }

  const tagCandidates = [
    event.tags?.status_code,
    event.tags?.http_status_code,
    event.tags?.['http.status_code'],
  ];
  const tagStatusCode = tagCandidates.find((value) => value !== undefined);
  if (tagStatusCode) {
    const parsedTagStatusCode = Number(tagStatusCode);
    if (!Number.isNaN(parsedTagStatusCode)) {
      return parsedTagStatusCode;
    }
  }

  const textCandidates = [
    event.message,
    ...(event.exception?.values?.map((value) => value.value ?? '') ?? []),
  ];
  // Apollo/GraphQL messages read "...status code 500"; httpClientIntegration's
  // own format reads "...status code: 422" (with a colon) — the colon is
  // optional here so both match.
  const match = textCandidates
    .filter(Boolean)
    .map((value) => value.match(/status code:?\s*(\d{3})/i))
    .find(Boolean);

  return match ? Number(match[1]) : undefined;
};

/**
 * The HTTP method of the request an `httpClientIntegration` event describes,
 * upper-cased. Empty string when the event carries no request — every caller
 * compares against a literal, so an absent method simply never matches.
 */
export const extractSentryEventRequestMethod = (
  event: SentryEventLike
): string => event.request?.method?.toUpperCase() ?? '';

/**
 * The path of the request URL, without query or fragment. Returns '' when the
 * URL is absent or unparseable, which no caller treats as a match.
 *
 * Parsed rather than pattern-matched so callers can anchor on the whole path
 * (`=== '/'`) instead of a substring test that '/foo' would also satisfy.
 */
export const extractSentryEventUrlPath = (event: SentryEventLike): string => {
  const url = extractSentryEventUrl(event);
  if (!url) return '';

  try {
    // `base` covers a relative URL; it is never read for an absolute one.
    return new URL(url, 'https://placeholder.invalid').pathname;
  } catch {
    return '';
  }
};
